import Utils from '@/core/utils/Utils';

/**
 * Обёртка над опцией контрола VSuggestionSelect.
 *
 * Отображаемый текст всегда лежит в name — и для сущности с бека, и для
 * свободного ввода. value хранит сущность с бека; для свободного ввода value
 * пустой (null), значение живёт только в name.
 */
export default class SuggestionSelectOption {
  /** Идентификатор. Для свободного ввода — undefined. */
  public id: any;
  /** Отображаемый текст. Единственный источник строкового представления. */
  public name: string;
  /** Сущность с бека. Для свободного ввода — null. */
  public value: any;

  constructor(value: any) {
    if (Utils.isObject(value) === false) {
      // Свободный ввод (строка) — значение живёт в name, value пустой.
      this.id = undefined;
      this.name = value;
      this.value = null;
    } else if (value.id === undefined || value.id === null) {
      // Плоский объект { id, name } — наш же свободный ввод, вернувшийся
      // через v-model. Разворачиваем в name, сущностью не считаем.
      this.id = undefined;
      this.name = value.name;
      this.value = null;
    } else {
      // Сущность с бека — value как есть, name из её строкового представления.
      this.id = value.id;
      this.name = value.toString();
      this.value = value;
    }
  }

  toString(): string {
    return this.name;
  }

  /**
   * Экранирует спецсимволы HTML. Текст опции приходит с бека и уходит в
   * v-html — без экранирования разметка из данных исполнялась бы как своя.
   */
  private escapeHtml(text: string): string {
    return text
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  /** Значение взято с бека — true; свободный ввод — false. */
  get isExisting(): boolean {
    return this.value !== null;
  }

  /**
   * Значение для v-model: существующая опция — сущность с бека,
   * новая (свободный ввод) — плоский { id: undefined, name }.
   */
  get asModelValue(): any {
    if (this.isExisting === true) {
      return this.value;
    } else {
      return { id: this.id, name: this.name };
    }
  }

  /**
   * Возвращает размеченный текст, выделяя вхождение searchTerm тегом <strong>.
   * Подсветка — внутреннее дело опции: компонент лишь сообщает, что набрано.
   */
  getHighlightedText(searchTerm: string): string {
    const itemText = this.name;

    if (itemText === null || itemText === undefined) {
      return itemText;
    }

    // Приводим searchTerm к нижнему регистру для нечувствительного поиска.
    const lowerCaseSearchTerm = searchTerm.toLowerCase().trim();
    const index = itemText.toLowerCase().indexOf(lowerCaseSearchTerm);

    // Вхождение не найдено — текст без разметки.
    if (index === -1) {
      return this.escapeHtml(itemText);
    }

    /**
     * Здесь разделяем и властвуем.
     * Длину берём у lowerCaseSearchTerm: по нему искали индекс, он обрезан
     * trim'ом, и длина исходного searchTerm сдвигала бы границы выделения.
     */
    const beforeMatch = itemText.slice(0, index);
    const match       = itemText.slice(index, index + lowerCaseSearchTerm.length);
    const afterMatch  = itemText.slice(index + lowerCaseSearchTerm.length);

    /** Почему именно match выделяем? потому что наш поиск возвращает
     * даже те варианты, где вхождение находится в середине или в конце строки,
     * потому нам не получится сделать как в гугле, потому что
     * у гугла все опшены начинаются с вхождения, и там можно выделять весь afterMatch,
     * у нас бы такое смотрелось бы глупо */
    return `${this.escapeHtml(beforeMatch)}<strong>${this.escapeHtml(match)}</strong>${this.escapeHtml(afterMatch)}`;
  }
}